import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import DecartResultPreview from './DecartResultPreview';
import { decartEdit } from '../Services/ApiServices/chatServices';
import '../Components/Styles/VideoEditor.css';

const VideoEditor = ({ video, onSave, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const videoSrc = typeof video === 'string' ? video : video?.src || video;
  const recordedDuration = typeof video === 'object' ? video?.recordedDuration : null;
  const previousTrimStart = typeof video === 'object' ? video?.trimStart : null;
  const previousTrimEnd = typeof video === 'object' ? video?.trimEnd : null;
  const previousFilter = typeof video === 'object' ? video?.filter : null;
  const previousVolume = typeof video === 'object' ? video?.volume : null;
  
  const [duration, setDuration] = useState(recordedDuration || 0);
  const [startTime, setStartTime] = useState(previousTrimStart || 0);
  const [endTime, setEndTime] = useState(previousTrimEnd || recordedDuration || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(previousVolume || 1);
  const [filter, setFilter] = useState(previousFilter || 'none');
  const [format, setFormat] = useState('mp4');
  const [activeTab, setActiveTab] = useState('trim'); // trim, adjust, format, decart
  const [isProcessing, setIsProcessing] = useState(false);

  const [decartPrompt, setDecartPrompt] = useState('');
  const [decartMode, setDecartMode] = useState('lucy');
  const [isDecartProcessing, setIsDecartProcessing] = useState(false);
  const [decartResult, setDecartResult] = useState(null);
  const [showDecartPreview, setShowDecartPreview] = useState(false);
  const [decartError, setDecartError] = useState(null);

  // Available filters
  const filters = [
    { value: 'none', label: 'None', filter: '' },
    { value: 'grayscale', label: 'Grayscale', filter: 'grayscale(100%)' },
    { value: 'sepia', label: 'Sepia', filter: 'sepia(100%)' },
    { value: 'contrast', label: 'High Contrast', filter: 'contrast(150%)' },
    { value: 'brightness', label: 'Bright', filter: 'brightness(120%)' },
    { value: 'saturate', label: 'Saturate', filter: 'saturate(150%)' },
    { value: 'blur', label: 'Blur', filter: 'blur(3px)' }
  ];

  // Initialize video metadata - separate from trim boundary logic
  useEffect(() => {
    if (videoRef.current) {
      let retryCount = 0;
      const maxRetries = 5;
      
      const handleLoadedMetadata = () => {
        const dur = videoRef.current?.duration;
        console.log('Video metadata loaded, duration:', dur, 'readyState:', videoRef.current?.readyState);
        if (dur && !isNaN(dur) && isFinite(dur) && dur > 0) {
          setDuration(dur);
          // Only set endTime if not previously set (first time editing)
          if (!previousTrimEnd && endTime === 0) {
            setEndTime(dur);
          }
          console.log('✅ Video duration successfully set to:', dur);
        } else if (recordedDuration && recordedDuration > 0) {
          // Fallback to recorded duration if metadata doesn't have valid duration
          console.warn('⚠️ Video metadata duration invalid, using recorded duration:', recordedDuration);
          setDuration(recordedDuration);
          if (!previousTrimEnd && endTime === 0) {
            setEndTime(recordedDuration);
          }
        } else {
          console.warn('⚠️ Duration not valid yet, will retry...');
        }
      };

      const handleDurationChange = () => {
        const dur = videoRef.current?.duration;
        console.log('🔄 Duration changed event, duration:', dur);
        if (dur && !isNaN(dur) && isFinite(dur) && dur > 0) {
          setDuration(dur);
          // Only set endTime if not previously set
          if (!previousTrimEnd && endTime === 0) {
            setEndTime(dur);
          }
          console.log('✅ Video duration set from durationchange:', dur);
        }
      };

      const handleCanPlay = () => {
        const dur = videoRef.current?.duration;
        console.log('🎥 Video can play, duration:', dur);
        if (dur && !isNaN(dur) && isFinite(dur) && dur > 0) {
          setDuration(dur);
          // Only set endTime if not previously set
          if (!previousTrimEnd && endTime === 0) {
            setEndTime(dur);
          }
          console.log('✅ Video duration set from canplay:', dur);
        } else if (recordedDuration && recordedDuration > 0 && duration === 0) {
          // Use recorded duration if still no valid duration from video
          console.log('✅ Using recorded duration from canplay:', recordedDuration);
          setDuration(recordedDuration);
          if (!previousTrimEnd && endTime === 0) {
            setEndTime(recordedDuration);
          }
        }
      };

      const retryLoadingDuration = () => {
        if (retryCount >= maxRetries) {
          if (recordedDuration && recordedDuration > 0) {
            console.warn('⚠️ Video metadata load failed, using recorded duration:', recordedDuration);
            setDuration(recordedDuration);
            if (!previousTrimEnd && endTime === 0) {
              setEndTime(recordedDuration);
            }
          } else {
            console.error('❌ Failed to load video duration after', maxRetries, 'retries');
          }
          return;
        }

        retryCount++;
        const delay = retryCount * 500; // Increase delay with each retry
        
        setTimeout(() => {
          if (videoRef.current) {
            const dur = videoRef.current.duration;
            console.log(`🔄 Retry ${retryCount}/${maxRetries} - Duration:`, dur);
            
            if (dur && !isNaN(dur) && isFinite(dur) && dur > 0) {
              setDuration(dur);
              if (!previousTrimEnd && endTime === 0) {
                setEndTime(dur);
              }
              console.log('✅ Video duration set on retry', retryCount, ':', dur);
            } else {
              // Try to trigger metadata load by seeking
              try {
                videoRef.current.currentTime = 0.01;
              } catch (e) {
                console.error('Error seeking video:', e);
              }
              retryLoadingDuration();
            }
          }
        }, delay);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        if (videoRef.current) {
          videoRef.current.currentTime = startTime;
        }
      };

      const handleError = (e) => {
        console.error('❌ Video error:', e);
        if (videoRef.current?.error) {
          console.error('Video error code:', videoRef.current.error.code);
          console.error('Video error message:', videoRef.current.error.message);
        }
      };

      videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoRef.current.addEventListener('durationchange', handleDurationChange);
      videoRef.current.addEventListener('canplay', handleCanPlay);
      videoRef.current.addEventListener('ended', handleEnded);
      videoRef.current.addEventListener('error', handleError);

      // Try multiple approaches to load metadata
      if (videoRef.current.readyState >= 1) {
        // Metadata already loaded
        handleLoadedMetadata();
      } else {
        // Force the video to load
        videoRef.current.load();
      }

      // If we have recorded duration, set it immediately as a fallback
      if (recordedDuration && recordedDuration > 0 && duration === 0) {
        console.log('📹 Setting initial duration from recording:', recordedDuration);
        setDuration(recordedDuration);
        if (!previousTrimEnd && endTime === 0) {
          setEndTime(recordedDuration);
        }
      }

      // Start retry mechanism
      retryLoadingDuration();

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
          videoRef.current.removeEventListener('durationchange', handleDurationChange);
          videoRef.current.removeEventListener('canplay', handleCanPlay);
          videoRef.current.removeEventListener('ended', handleEnded);
          videoRef.current.removeEventListener('error', handleError);
        }
      };
    }
  }, [video, recordedDuration]);

  // Enforce trim boundaries during playback - separate useEffect
  useEffect(() => {
    if (!videoRef.current) return;

    const handleTimeUpdate = () => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
        
        // Enforce trim boundaries during playback
        if (duration > 0 && endTime > 0) {
          if (videoRef.current.currentTime < startTime) {
            videoRef.current.currentTime = startTime;
          } else if (videoRef.current.currentTime >= endTime) {
            videoRef.current.pause();
            videoRef.current.currentTime = startTime;
            setIsPlaying(false);
          }
        }
      }
    };

    videoRef.current.addEventListener('timeupdate', handleTimeUpdate);
    
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };
  }, [startTime, endTime, duration]);

  // Apply filter to video
  useEffect(() => {
    if (videoRef.current) {
      const selectedFilter = filters.find(f => f.value === filter);
      videoRef.current.style.filter = selectedFilter ? selectedFilter.filter : '';
    }
  }, [filter, filters]);

  // Update volume
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        // If at end time, reset to start time
        if (currentTime >= endTime || currentTime < startTime) {
          videoRef.current.currentTime = startTime;
        }
        videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
          setIsPlaying(false);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (value) => {
    const time = parseFloat(value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleStartTimeChange = (value) => {
    const time = parseFloat(value);
    
    // Ensure startTime is valid
    if (time >= endTime) {
      // Don't allow startTime to be greater than or equal to endTime
      const maxStartTime = Math.max(endTime - 0.5, 0);
      setStartTime(maxStartTime);
      if (videoRef.current) {
        videoRef.current.currentTime = maxStartTime;
        setCurrentTime(maxStartTime);
      }
      return;
    }
    
    setStartTime(time);
    
    // Always move playback to the new start time
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleEndTimeChange = (value) => {
    const time = parseFloat(value);
    
    // Ensure endTime is valid
    if (time <= startTime) {
      // Don't allow endTime to be less than or equal to startTime
      const minEndTime = Math.min(startTime + 0.5, duration);
      setEndTime(minEndTime);
      return;
    }
    
    setEndTime(time);
    
    // If current playback position is beyond new endTime, adjust it
    if (videoRef.current && currentTime >= time) {
      videoRef.current.pause();
      videoRef.current.currentTime = time - 0.1; // Set slightly before end
      setCurrentTime(time - 0.1);
      setIsPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Process and save video with edit metadata
   * For client-side limitations, we save original video with edit instructions
   * Full video processing would require server-side or FFmpeg.js
   */
  const handleSave = async () => {
    if (!duration || duration === 0) {
      alert('Video duration not loaded yet. Please wait a moment and try again.');
      return;
    }

    if (startTime >= endTime) {
      alert('Start time must be before end time. Please adjust your trim settings.');
      return;
    }

    if (endTime - startTime < 0.5) {
      alert('Trimmed video must be at least 0.5 seconds long.');
      return;
    }

    setIsProcessing(true);
    
    try {
      const trimmedDuration = endTime - startTime;
      
      const editedVideo = {
        src: videoSrc, // Keep original video source to avoid security issues
        format: format,
        trimStart: startTime,
        trimEnd: endTime,
        duration: trimmedDuration,
        volume: volume,
        filter: filter,
        edited: true,
        timestamp: new Date().toISOString(),
        estimatedSize: Math.round((videoSrc.length * trimmedDuration) / duration)
      };

      console.log('Saving edited video:', editedVideo);
      onSave(editedVideo);
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Failed to save video. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setStartTime(0);
    setEndTime(duration);
    setVolume(1);
    setFilter('none');
    setFormat('mp4');
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.style.filter = '';
    }
  };

  const dataURLtoBlob = (dataURL) => {
    if (!dataURL) return null;
    const parts = dataURL.split(';base64,');
    const contentType = parts[0].split(':')[1] || 'video/webm';
    const byteCharacters = atob(parts[1]);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  };

  const handleDecartProcess = async () => {
    if (!videoSrc) {
      setDecartError('No video available for processing');
      return;
    }
    setIsDecartProcessing(true);
    setDecartError(null);
    try {
      const blob = dataURLtoBlob(videoSrc);
      if (!blob || blob.size === 0) {
        throw new Error('Failed to convert video to blob');
      }
      console.log('[DEBUG] Video blob size:', blob.size, 'type:', blob.type);
      
      const file = new File([blob], 'editor-video.webm', { type: 'video/webm' });
      console.log('[DEBUG] Video file:', file.name, file.size, file.type);
      
      const promptValue = decartPrompt && decartPrompt.trim().length > 0
        ? decartPrompt
        : 'Apply professional video enhancement, improve lighting and color grading';
      const modeValue = ['lucy', 'virtual-try-on'].includes(decartMode) ? decartMode : 'lucy';
      console.log('[DEBUG] Prompt:', promptValue, 'Mode:', modeValue);
      
      const formData = new FormData();
      formData.append('video', file);
      formData.append('prompt', promptValue);
      formData.append('mode', modeValue);
      
      await decartEdit(formData, (res) => {
        console.log('[DEBUG] API response status:', res?.status);
        if (res?.data?.data) {
          setDecartResult(res.data.data);
          setShowDecartPreview(true);
        } else if (res?.data) {
          setDecartResult(res.data);
          setShowDecartPreview(true);
        } else {
          throw new Error('Invalid response from Decart API - no data returned');
        }
      }, (err) => {
        const errorMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to process video with Decart AI';
        console.error('[DEBUG] API error details:', err?.response);
        setDecartError(errorMsg);
        throw new Error(errorMsg);
      });
    } catch (error) {
      console.error('[DEBUG] Decart processing error:', error.message);
      setDecartError(error.message);
    } finally {
      setIsDecartProcessing(false);
    }
  };

  const handleDecartSave = (resultData) => {
    onSave({
      src: resultData.src,
      type: 'decart',
      format: resultData.format || 'jpeg',
      size: resultData.size,
      edited: true,
      timestamp: new Date().toISOString()
    });
    setShowDecartPreview(false);
    setDecartResult(null);
    setDecartPrompt('');
  };

  const handleDecartCancel = () => {
    setShowDecartPreview(false);
    setDecartResult(null);
    setDecartError(null);
  };

  return (
    <div className="video-editor-overlay">
      <div className="video-editor-modal">
        <div className="video-editor-header">
          <h3>🎬 Edit Video</h3>
          <button 
            className="editor-close-btn" 
            onClick={onCancel}
            aria-label="Close editor"
          >
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="editor-tabs">
          <button
            className={`editor-tab ${activeTab === 'trim' ? 'active' : ''}`}
            onClick={() => setActiveTab('trim')}
          >
            ✂️ Trim
          </button>
          <button
            className={`editor-tab ${activeTab === 'adjust' ? 'active' : ''}`}
            onClick={() => setActiveTab('adjust')}
          >
            🎨 Adjust
          </button>
          <button
            className={`editor-tab ${activeTab === 'format' ? 'active' : ''}`}
            onClick={() => setActiveTab('format')}
          >
            💾 Format
          </button>
          <button
            className={`editor-tab ${activeTab === 'decart' ? 'active' : ''}`}
            onClick={() => setActiveTab('decart')}
          >
            🤖 Decart AI
          </button>
        </div>

        {/* Video Preview */}
        <div className="video-preview-container">
          <video
            ref={videoRef}
            src={videoSrc}
            className="video-preview"
            preload="auto"
            playsInline
            muted={false}
            crossOrigin="anonymous"
            onLoadedMetadata={() => console.log('📹 onLoadedMetadata callback fired')}
            onLoadedData={() => console.log('📹 onLoadedData callback fired')}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        {/* Playback Controls */}
        <div className="playback-controls">
          <button
            className="play-pause-btn"
            onClick={togglePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? '⏸️' : '▶️'}
          </button>
          <div className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={currentTime}
            onChange={(e) => handleSeek(e.target.value)}
            className="seek-bar"
          />
        </div>

        {/* Tab Content */}
        <div className="editor-content">
          {/* Trim Tab */}
          {activeTab === 'trim' && (
            <div className="trim-controls">
              <div className="trim-section">
                <label>
                  ⏪ Start Time: {formatTime(startTime)}
                  <input
                    type="range"
                    min="0"
                    max={duration > 0 ? Math.max(0, endTime - 0.5) : 100}
                    step="0.1"
                    value={startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    className="trim-slider"
                    disabled={!duration || duration === 0}
                  />
                </label>
              </div>

              <div className="trim-section">
                <label>
                  ⏩ End Time: {formatTime(endTime)}
                  <input
                    type="range"
                    min={duration > 0 ? Math.min(duration, startTime + 0.5) : 0}
                    max={duration || 100}
                    step="0.1"
                    value={endTime}
                    onChange={(e) => handleEndTimeChange(e.target.value)}
                    className="trim-slider"
                    disabled={!duration || duration === 0}
                  />
                </label>
              </div>

              <div className="trim-info">
                {duration > 0 ? (
                  <p>Trimmed Duration: <strong>{formatTime(endTime - startTime)}</strong></p>
                ) : (
                  <div>
                    <p className="loading-text" style={{color: '#ffa500'}}>⏳ Loading video duration...</p>
                    <p style={{color: '#888', fontSize: '0.85em', marginTop: '5px'}}>
                      If this takes too long, try using a different browser or check the console for errors.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Adjust Tab */}
          {activeTab === 'adjust' && (
            <div className="adjust-controls">
              <div className="control-section">
                <label>
                  🔊 Volume
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="volume-slider"
                  />
                  <span>{Math.round(volume * 100)}%</span>
                </label>
              </div>

              <div className="control-section">
                <label>🎨 Filter</label>
                <div className="filter-grid">
                  {filters.map((f) => (
                    <button
                      key={f.value}
                      className={`filter-btn ${filter === f.value ? 'active' : ''}`}
                      onClick={() => setFilter(f.value)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Format Tab */}
          {activeTab === 'format' && (
            <div className="format-controls">
              <div className="control-section">
                <label>📹 Output Format</label>
                <div className="format-options">
                  <label className="format-option">
                    <input
                      type="radio"
                      value="mp4"
                      checked={format === 'mp4'}
                      onChange={(e) => setFormat(e.target.value)}
                    />
                    <span>MP4 (H.264)</span>
                    <small>Best compatibility</small>
                  </label>
                  <label className="format-option">
                    <input
                      type="radio"
                      value="webm"
                      checked={format === 'webm'}
                      onChange={(e) => setFormat(e.target.value)}
                    />
                    <span>WebM</span>
                    <small>Smaller size</small>
                  </label>
                </div>
              </div>

              <div className="format-info">
                <p>ℹ️ Note: Format conversion requires additional processing and may take time depending on video length.</p>
              </div>
            </div>
          )}

          {activeTab === 'decart' && (
            <div className="control-section">
              <div className="control-group">
                <label>🤖 Decart AI Prompt (optional)</label>
                <input
                  type="text"
                  className="decart-prompt-input"
                  placeholder="Enter AI enhancement prompt..."
                  value={decartPrompt}
                  onChange={(e) => setDecartPrompt(e.target.value)}
                />
              </div>
              <div className="control-group">
                <label>🎯 Processing Mode</label>
                <select
                  className="decart-mode-select"
                  value={decartMode}
                  onChange={(e) => setDecartMode(e.target.value)}
                >
                  <option value="lucy">Lucy (General Edit)</option>
                  <option value="virtual-try-on">Virtual Try-On</option>
                </select>
              </div>
              {decartError && (
                <div className="decart-error-message" style={{ color: '#ff4444', marginTop: '8px', fontSize: '13px' }}>
                  ⚠️ {decartError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="editor-actions">
          <button 
            className="editor-btn secondary" 
            onClick={handleReset}
            disabled={isProcessing}
          >
            🔄 Reset
          </button>
          <button 
            className="editor-btn secondary" 
            onClick={onCancel}
            disabled={isProcessing}
          >
            ❌ Cancel
          </button>
          <button 
            className="editor-btn decart-process-btn"
            onClick={handleDecartProcess}
            disabled={isDecartProcessing}
            style={{ display: activeTab === 'decart' ? 'flex' : 'none' }}
          >
            {isDecartProcessing ? '⏳ Processing...' : '🤖 Decart AI'}
          </button>
          <button 
            className="editor-btn primary" 
            onClick={handleSave}
            disabled={isProcessing}
            style={{ display: activeTab === 'decart' ? 'none' : 'flex' }}
          >
            {isProcessing ? '⏳ Processing...' : '💾 Save Video'}
          </button>
        </div>
      </div>

      {showDecartPreview && decartResult && (
        <DecartResultPreview
          result={decartResult}
          isVideo={true}
          onSave={handleDecartSave}
          onCancel={handleDecartCancel}
        />
      )}
    </div>
  );
};

VideoEditor.propTypes = {
  video: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default VideoEditor;
